import Dexie, { type Table } from 'dexie';
import type { Entity, Link } from '@/types/entity';
import type { Command, BeatAssistEdit, BeatAssistMode } from '@/types/ai';
import type { Project } from '@/types/project';
import { DB_NAME } from '@/lib/constants';
import type { EngineRunReport } from '@/engine/canonBundle';
import type { AutowriteAttempt, AutowriteStop } from '@/engine/tauriBridge';

export interface Snapshot {
  id: string;
  ts: number;
  reason: string;
  bundle: unknown; // CodexExportBundle
}

/** 사용자 업로드 시각 에셋 — 행성 텍스처·인물 초상·세력 문장 등. Blob 로컬 저장. */
export type AssetKind = 'portrait' | 'planet' | 'sigil' | 'location' | 'nebula' | 'cover' | 'city' | 'city-depth';
export interface Asset {
  id: string;
  entityId?: string;
  kind: AssetKind;
  blob: Blob;
  mime: string;
  name?: string;
  createdAt: number;
}

/** 오케스트레이션 콘솔(WS2) — 프롬프트→4레인(Claude/Codex/Claude팀/Codex팀) 컴포저+수집기. */
export type OrchestrationLane = 'claude' | 'codex' | 'claude-team' | 'codex-team';
export interface LaneResult {
  lane: OrchestrationLane;
  status: 'pending' | 'done';
  result: string; // 붙여넣기/회수된 결과
}
export interface OrchestrationRun {
  id: string;
  title: string;
  prompt: string; // 작가가 입력한 과업
  lanes: OrchestrationLane[]; // 선택된 레인
  contextIds: string[]; // 주입된 엔티티 id(맥락)
  spec: string; // 구성된 전체 실행 사양(맥락+레인별 지시)
  results: LaneResult[]; // 레인별 회수 결과
  status: 'composed' | 'collecting' | 'done';
  createdAt: number;
  updatedAt: number;
  // --- v4: 비트 보조(레이어 C/D) — 오케스트레이션과 한 substrate, kind로 구분 ---
  kind?: 'orchestration' | 'beat-assist'; // 미지정(구행) = orchestration
  beatId?: string; // beat-assist: 대상 비트
  mode?: BeatAssistMode; // beat-assist: 보조 모드
  suggestions?: BeatAssistEdit[]; // beat-assist: 회수·파싱된 제안(상태 포함)
}

/** 정합성 엔진 실행 감사 로그 — canon export와 분리. */
export interface EngineRunRecord {
  id: string;
  projectId: string;
  createdAt: number;
  bundleHash: string;
  report: EngineRunReport;
}

/** 콘솔 이벤트(에이전트 세션 영속, 재설계 R3) — 비-canon, export bundle과 분리. 전송 시 휘발 금지. */
export interface ConsoleEventRecord {
  seq?: number; // Dexie 자동 증가(타임라인 순서)
  id: string;
  projectId: string;
  at: number;
  role: 'user' | 'agent' | 'system';
  type: 'prompt' | 'system' | 'assistant' | 'tool_use' | 'tool_result' | 'result' | 'error' | 'raw' | 'proposal';
  text?: string;
  tool?: string;
  ok?: boolean;
  /** 사용자 prompt에 컨텍스트 프리앰블(현재 비트)이 주입됐는지 — UI 칩 표시용 */
  contextNote?: string;
  /** type='proposal'일 때 proposalBundles 레코드 id(K1) — 타임라인이 라이브 상태를 조회 */
  proposalId?: string;
}

// ── AI-native K1: 커널 제안 묶음(Δ + 파급 + 지점별 제안) — 승인 큐의 단위. 비-canon. ──
export type ProposalDeltaOp =
  | { op: 'set_fact'; scope: string; fact: string }
  | { op: 'clear_fact'; scope: string; fact: string }
  | { op: 'rename_fact'; scope: string; from: string; to: string }
  | { op: 'collapse_resource'; resource: string; scope: string }
  | { op: 'uncollapse_resource'; resource: string; scope: string }
  | { op: 'set_institution_status'; institution: string; status: string };

export interface ProposalRippleItem {
  beat: string;
  kind: string;
  severity: string;
  evidence: string;
  canon_refs: string[];
}

export interface ProposalFix {
  beatId: string;
  suggestion: string;
}

export interface ProposalBundleRecord {
  id: string;
  projectId: string;
  createdAt: number;
  status: 'proposed' | 'accepted' | 'rejected';
  decidedAt?: number;
  /** 수락 시 UI가 발급(K1 v1 — Rust 경계 이관은 후속). 감사 추적용. */
  approvalId?: string;
  /** K3·K5-d: 'delta'=세계 사실/거시 Δ(기본) / 'scene'=씬 제안 / 'patch'=엔티티·관계·메타 구체 수정 */
  kind?: 'delta' | 'scene' | 'patch';
  title: string;
  intent?: string;
  delta: ProposalDeltaOp[];
  ripple: ProposalRippleItem[];
  fixes: ProposalFix[];
  /** kind='scene'의 페이로드 — 봉인 게이트(사이드카) 통과분만 도착한다 */
  scene?: { beatId: string; title?: string; prose: string };
  /** kind='patch'의 페이로드(K5-d) — 도착 시 zod 검증(proposal.schema) 통과분만 적재된다 */
  patches?: import('@/lib/schemas/proposal.schema').ProposalPatchOp[];
  /** 수락 적용 결과 요약(어떤 op가 어디에 닿았나) — 감사 */
  appliedNotes?: string[];
}

// ── 자동집필 세션(K3/P3 토대): 한 회전 카드가 비트 전환/리로드로 사라지지 않게 시도 이력을 영속한다. ──
export type AutowriteSessionStatus = 'running' | 'awaiting_author' | 'accepted' | 'rejected' | 'stopped' | 'error';
export type AutowriteSessionStopReason = AutowriteStop | 'RepeatedFailure' | 'MaxAttempts' | 'RunError' | 'ReviewerReject';

export interface AutowriteSessionAttemptRecord {
  attemptNo: number;
  at: number;
  targetScope?: string;
  draftHashes: string[];
  verdictCount: number;
  passed: number;
  stopReasons: AutowriteStop[];
  repeatedDraftHashes: string[];
  attempt: AutowriteAttempt | null;
  error?: string;
}

export interface AutowriteSessionRecord {
  id: string;
  projectId: string;
  beatId: string;
  createdAt: number;
  updatedAt: number;
  status: AutowriteSessionStatus;
  decidedAt?: number;
  maxAttempts: number;
  attempts: AutowriteSessionAttemptRecord[];
  repeatedDraftHashes: string[];
  targetScope?: string;
  stopReason?: AutowriteSessionStopReason;
  lastError?: string;
}

/** 영속 진실 원천 (02 §6). 모든 커밋의 종착점. */
export class CodexDB extends Dexie {
  entities!: Table<Entity, string>;
  links!: Table<Link, string>;
  project!: Table<Project, string>;
  commands!: Table<Command, string>; // 쓰기 로그(감사)
  snapshots!: Table<Snapshot, string>; // 파괴적 동작 전 백업
  assets!: Table<Asset, string>; // 사용자 업로드 시각 에셋(Blob)
  orchestrationRuns!: Table<OrchestrationRun, string>; // 오케스트레이션 실행(WS2)
  engineRuns!: Table<EngineRunRecord, string>; // 정합성 엔진 감사 로그(비-canon)
  consoleEvents!: Table<ConsoleEventRecord, number>; // 콘솔 에이전트 세션(R3, 비-canon)
  proposalBundles!: Table<ProposalBundleRecord, string>; // 커널 제안 묶음 = 승인 큐(K1, 비-canon)
  autowriteSessions!: Table<AutowriteSessionRecord, string>; // 자동집필 시도 이력(K3/P3 토대, 비-canon)

  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
    });
    // v2 — assets 테이블 추가(기존 테이블 보존, Dexie 자동 마이그레이션)
    this.version(2).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
    });
    // v3 — orchestrationRuns 테이블 추가(WS2). 기존 보존, Dexie 자동 마이그레이션.
    this.version(3).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt',
    });
    // v4 — orchestrationRuns에 beatId 인덱스 추가(비트 보조 조회). 필드(kind/mode/suggestions)는
    // 비인덱스라 스키마 무변경, 구행(beatId=undefined·kind=undefined)은 무해. additive.
    this.version(4).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt, beatId',
    });
    // v5 — engineRuns 감사 테이블. 엔진 실행 결과/제안은 canon이 아니므로 export bundle과 분리한다.
    this.version(5).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt, beatId',
      engineRuns: 'id, projectId, createdAt',
    });
    // v6 — consoleEvents(R3): 콘솔 에이전트 세션 영속(전송 시 휘발 금지). additive.
    this.version(6).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt, beatId',
      engineRuns: 'id, projectId, createdAt',
      consoleEvents: '++seq, projectId, at',
    });
    // v7 — proposalBundles(K1): 커널 제안 묶음(Δ+파급+제안) = 승인 큐. additive.
    this.version(7).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt, beatId',
      engineRuns: 'id, projectId, createdAt',
      consoleEvents: '++seq, projectId, at',
      proposalBundles: 'id, projectId, status, createdAt',
    });
    // v8 — autowriteSessions: 자동집필 한 회전 결과와 반복 실패 신호를 영속한다. additive.
    this.version(8).stores({
      entities: 'id, type, name, *tags, canonStatus, deletedAt, updatedAt',
      links: 'id, from, to, type, deletedAt',
      project: 'id',
      commands: 'id, ts, kind',
      snapshots: 'id, ts',
      assets: 'id, entityId, kind, createdAt',
      orchestrationRuns: 'id, status, createdAt, beatId',
      engineRuns: 'id, projectId, createdAt',
      consoleEvents: '++seq, projectId, at',
      proposalBundles: 'id, projectId, status, createdAt',
      autowriteSessions: 'id, projectId, beatId, status, createdAt, updatedAt',
    });
  }
}

export const db = new CodexDB();
